import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowLeft, ChevronDown, ChevronRight, Lock, Plus, Search } from "lucide-react";
import { type ReactNode, useState } from "react";
import { DocTree } from "../features/tree/DocTree";
import type { Space } from "../lib/api/models";
import { can, useMe } from "../lib/auth";
import { useClients, useSpaces } from "../lib/queries";
import { ADMIN_SECTIONS } from "./admin-sections";
import { useOpenTopics } from "./useOpenTopics";
import { useSection } from "./useSection";

const ROW = "rounded-lg px-2.5 py-2 text-base text-ink";
const NOTE = "px-2.5 text-sm text-ink-3";
const LABEL = "text-xs font-semibold text-ink-3";

/** The column's frame: a title, then the list or the tree. */
function Frame({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-3 py-5">
      <div className="flex items-baseline justify-between gap-2 px-2">
        <div className="min-w-0 truncate text-xl font-semibold text-ink">{title}</div>
      </div>
      {children}
    </div>
  );
}

function ClientList({ activeId, onNavigate }: { activeId?: string; onNavigate?: () => void }) {
  const { data: clients = [], isLoading } = useClients();
  const [filter, setFilter] = useState("");
  const f = filter.trim().toLowerCase();
  const shown = f ? clients.filter((c) => c.name.toLowerCase().includes(f)) : clients;
  return (
    <nav aria-label="Clients" className="flex flex-col gap-0.5">
      <label className="mb-2 flex h-9 items-center gap-2 rounded-lg border border-line bg-surface px-2.5 text-ink-3 focus-within:border-green">
        <Search size={14} className="shrink-0" aria-hidden="true" />
        <input
          aria-label="Filter clients"
          placeholder={`Filter ${clients.length} clients`}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-3"
        />
      </label>
      {isLoading ? <p className={NOTE}>Loading…</p> : null}
      {!isLoading && clients.length === 0 ? <p className={NOTE}>No clients yet.</p> : null}
      {f && clients.length > 0 && shown.length === 0 ? (
        <p className={NOTE}>No client matches “{filter.trim()}”.</p>
      ) : null}
      {shown.map((c) => {
        const active = c.id === activeId;
        return (
          <Link
            key={c.id}
            to="/clients/$clientId"
            params={{ clientId: c.id }}
            onClick={onNavigate}
            aria-current={active ? "page" : undefined}
            className={clsx(ROW, active ? "bg-green-wash" : "hover:bg-desk")}
          >
            <span className="flex items-center justify-between gap-2">
              <span className={clsx("truncate", active && "font-semibold")}>{c.name}</span>
              {c.restricted ? (
                <span className="inline-flex shrink-0 items-center text-ink-3">
                  <Lock size={12} aria-hidden="true" />
                  <span className="sr-only">Restricted</span>
                </span>
              ) : null}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * The Handbook's topics (handbook start spec §4.1). The arrow shows a topic's documents under its row and
 * does not navigate; the name is the link to the topic page. A closed topic does not load its tree.
 */
function InternalSpaces({
  current,
  activeDocId,
  onNavigate,
}: {
  current: Space | null;
  activeDocId?: string;
  onNavigate?: () => void;
}) {
  const { data: spaces = [], isLoading } = useSpaces();
  const internal = spaces.filter((s) => s.kind === "internal");
  const { open, toggle } = useOpenTopics(current?.kind === "internal" ? current.id : undefined);
  return (
    <nav aria-label="Topics" className="flex flex-col gap-0.5">
      {isLoading ? <p className={NOTE}>Loading…</p> : null}
      <ul className="flex flex-col gap-0.5">
        {internal.map((s) => {
          const active = current?.id === s.id;
          const isOpen = open.has(s.id);
          // The wash marks the page that is open: the topic's own page, or else the document in its tree.
          const onTopicPage = active && !activeDocId;
          return (
            <li key={s.id}>
              <div
                className={clsx(
                  "flex items-stretch rounded-lg text-base text-ink",
                  onTopicPage ? "bg-green-wash" : "hover:bg-desk",
                  active && "font-semibold",
                )}
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={isOpen ? `topic-docs-${s.id}` : undefined}
                  aria-label={`${isOpen ? "Hide" : "Show"} documents in ${s.name}`}
                  onClick={() => toggle(s.id)}
                  className="grid w-9 shrink-0 place-items-center rounded-lg text-ink-3 hover:text-ink"
                >
                  {isOpen ? (
                    <ChevronDown size={14} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={14} aria-hidden="true" />
                  )}
                </button>
                <Link
                  to="/topics/$slug"
                  params={{ slug: s.slug }}
                  onClick={onNavigate}
                  aria-current={active ? "page" : undefined}
                  className="flex min-w-0 flex-1 items-center justify-between gap-2 py-2 pr-2.5"
                >
                  <span className="truncate">{s.name}</span>
                  {s.restricted ? (
                    <span className="inline-flex shrink-0 items-center text-ink-3">
                      <Lock size={12} aria-hidden="true" />
                      <span className="sr-only">Restricted</span>
                    </span>
                  ) : null}
                </Link>
              </div>
              {isOpen ? (
                <div id={`topic-docs-${s.id}`} className="ml-4 border-l border-line pl-1">
                  <DocTree space={s} activeId={active ? activeDocId : undefined} onNavigate={onNavigate} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** A client's or the templates' document tree with its "New" action. */
function SpaceTree({
  space,
  activeDocId,
  onNavigate,
}: {
  space: Space;
  activeDocId?: string;
  onNavigate?: () => void;
}) {
  const me = useMe();
  const canCreate = space.kind === "templates" ? can(me, "admin") : can(me, "create");
  return (
    <section aria-label={`${space.name} documents`} className="flex flex-col gap-1">
      <div className="flex items-center justify-between px-2.5">
        <span className={LABEL}>{space.kind === "client" ? "Documents" : space.name}</span>
        {canCreate ? (
          <Link
            to="/new"
            search={{ space: space.id }}
            onClick={onNavigate}
            aria-label="New"
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs text-green-ink hover:bg-green-wash"
          >
            <Plus size={13} aria-hidden="true" /> New
          </Link>
        ) : null}
      </div>
      <DocTree space={space} activeId={activeDocId} onNavigate={onNavigate} />
    </section>
  );
}

function AdminSections({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav aria-label="Administration" className="flex flex-col gap-0.5">
      {ADMIN_SECTIONS.map((s) => (
        <Link
          key={s.to}
          to={s.to}
          onClick={onNavigate}
          className={clsx(ROW, "hover:bg-desk")}
          activeProps={{ className: "bg-green-wash! font-semibold" }}
        >
          {s.label}
        </Link>
      ))}
    </nav>
  );
}

/**
 * The second column (redesign spec §4.3). It follows the area: the client list, a client's document tree
 * under the client's name, the Handbook's topics with their documents under them, the admin sections, or the template tree
 * of an open template.
 */
export function Column({ onNavigate }: { onNavigate?: () => void }) {
  const { section, admin, clientId, space, activeDocId } = useSection();
  if (admin && space?.kind === "templates" && activeDocId) {
    return (
      <Frame
        title={
          <Link to="/admin/templates" onClick={onNavigate} className="flex items-center gap-1.5 hover:text-green-ink">
            <ArrowLeft size={15} className="shrink-0" aria-hidden="true" />
            <span className="truncate">Templates</span>
          </Link>
        }
      >
        <SpaceTree space={space} activeDocId={activeDocId} onNavigate={onNavigate} />
      </Frame>
    );
  }
  if (admin) {
    return (
      <Frame title="Admin">
        <AdminSections onNavigate={onNavigate} />
      </Frame>
    );
  }
  if (activeDocId && space?.kind === "client") {
    return (
      <Frame
        title={
          <Link
            to="/clients/$clientId"
            params={{ clientId: space.id }}
            onClick={onNavigate}
            className="flex items-center gap-1.5 hover:text-green-ink"
          >
            <ArrowLeft size={15} className="shrink-0" aria-hidden="true" />
            <span className="truncate">{space.name}</span>
          </Link>
        }
      >
        <SpaceTree space={space} activeDocId={activeDocId} onNavigate={onNavigate} />
      </Frame>
    );
  }
  if (section === "internal") {
    return (
      <Frame title="Handbook">
        <InternalSpaces current={space} activeDocId={activeDocId} onNavigate={onNavigate} />
      </Frame>
    );
  }
  return (
    <Frame title="Clients">
      <ClientList activeId={clientId} onNavigate={onNavigate} />
    </Frame>
  );
}
